import { signal } from '@angular/core';
import { PORTFOLIO_DUMMY, PORTFOLIO_PIECE_ICON } from 'src/app/pages/organizations/my-organization/portfolio/my-org-portfolio.data';
import { INVENTED_DATA_ENABLED } from './invented-data';

/**
 * Sample answers for Organization › Profile (`/organizationnew/**`) — the endpoints and fields
 * `backend/api` does not have yet. Every address is in `.claude-docs/org-profile-BACKEND-CONTRACT.md`.
 *
 * The wiring is in `profile-demo-backend.interceptor.ts`. Same rules as the Home's (Ansar, 28–30 Sep):
 *
 * 1. **Only requests made on the Profile pages**, and only for the addresses listed there. Home,
 *    Directories, Settings and every other page asking `/organization/{id}` or `/api/v3/services`
 *    are never touched.
 * 2. **A NEW endpoint** answering 404 gets a seeded 200. Every other status passes through.
 * 3. **A LIVE endpoint missing a field** gets that field only while the KEY IS ABSENT. A key the
 *    server sent — even `null` — is a real answer and is never replaced.
 * 4. **Writes to a NEW endpoint** (answered 404) are remembered for the browser session and echoed
 *    back, so the form behaves like a form; they are applied over the next read.
 * 5. **Everything substituted is labelled on screen**: `profileDemoUsed` names each source.
 *
 * **To remove it:** delete the three `profile-demo-backend*` files and the word
 * `profileDemoBackendInterceptor` from `withInterceptors([...])` in `app.config.ts`. The pages
 * import only `profileDemoUsed` / `PROFILE_SAMPLE_LABELS` from here (through their sample marks).
 */

/** Master switch. `false` lets every request through untouched. */
export const PROFILE_DEMO_ENABLED = true;

/** What a piece of sample data is about. One key per NEW endpoint or FILL field group. */
export type ProfileSampleSource = 'about' | 'hours' | 'since' | 'listing' | 'capabilities' | 'ratings' | 'portfolio' | 'team-functions';

export const PROFILE_SAMPLE_LABELS: Record<ProfileSampleSource, string> = {
  about: 'About text',
  hours: 'open hours and time zone',
  since: 'date joined',
  listing: 'public profile switch',
  capabilities: 'work done here, experience and skills',
  ratings: 'ratings',
  portfolio: 'portfolio pieces',
  'team-functions': 'team split by function',
};

/** Sources that produced sample data during this browser session, in first-seen order. */
export const profileDemoUsed = signal<ReadonlySet<ProfileSampleSource>>(new Set());

export function noteProfileSample(source: ProfileSampleSource): void {
  const current = profileDemoUsed();
  if (current.has(source)) return;
  profileDemoUsed.set(new Set([...current, source]));
}

// ─── What the reader wrote this session, and what live answers were seen ─────────────────────────

const written = new Map<string, any>();
/** The last services answer seen on a Profile page, so the capabilities seed describes real pairs. */
let seenServices: any[] = [];
/** Team sizes seen on `/organization/{id}/users`, so the function split adds up to the real team. */
const seenTeam = new Map<number, number>();

export function rememberWrite(path: string, body: any): any {
  const merged = { ...(written.get(path) ?? {}), ...(body ?? {}) };
  written.set(path, merged);
  return merged;
}

export function writtenAt(path: string): any {
  return written.get(path);
}

export function rememberServices(body: any): void {
  if (Array.isArray(body)) seenServices = body;
}

export function rememberTeam(orgId: number, body: any): void {
  const n = Array.isArray(body) ? body.length : Array.isArray(body?.data) ? body.data.length : null;
  if (n !== null) seenTeam.set(orgId, n);
}

/** Test hook: forget everything remembered. */
export function resetProfileDemo(): void {
  written.clear();
  seenServices = [];
  seenTeam.clear();
  profileDemoUsed.set(new Set());
}

const isAbsent = (o: any, key: string) => !o || !Object.prototype.hasOwnProperty.call(o, key);

// ─── LIVE: GET /organization/{id} ────────────────────────────────────────────────────────────────

/** The sample About text (the mockup's own, about a language services company). */
export const SAMPLE_ABOUT = [
  'We are a language services company. We translate, dub, subtitle and record voice for publishers, ministries and media companies.',
  'Most of our work is long-form: courses, documentaries, audio Bibles and phone systems. A project manager stays with each account from the first file to the last delivery.',
].join('\n\n');

/**
 * Fills the profile fields the organization answer does not carry yet, while their keys are absent,
 * and lays over what the reader saved this session (the profile write is not stored anywhere yet).
 * Returns the new body, or null when nothing changed.
 */
export function fillOrganization(body: any, orgId: number, now: Date = new Date()): { body: any; used: ProfileSampleSource[] } | null {
  if (!body || typeof body.value !== 'string') return null;
  let value: any;
  try { value = JSON.parse(body.value); } catch { return null; }
  const used = new Set<ProfileSampleSource>();
  const fill = (key: string, v: any, source: ProfileSampleSource) => {
    if (isAbsent(value, key)) { value[key] = v; used.add(source); }
  };
  // The About text, open hours and join date are invented: only filled while inventions are on.
  if (INVENTED_DATA_ENABLED) {
    fill('largeInfo', SAMPLE_ABOUT, 'about');
    fill('timezone', 'America/Chicago', 'hours');
    fill('openFrom', '08:00', 'hours');
    fill('openTo', '18:00', 'hours');
    fill('openDays', ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], 'hours');
    // A year and seven months before today: the mockup's "Feb 2025 · 1 year 7 months".
    const joined = new Date(now.getFullYear(), now.getMonth() - 19, 3);
    fill('dateCreated', `${joined.getFullYear()}-${String(joined.getMonth() + 1).padStart(2, '0')}-03`, 'since');
  }
  const listing = writtenAt(`/organization/${orgId}/listing`);
  let defaulted = false;
  if (INVENTED_DATA_ENABLED || listing) {
    fill('isPublic', listing?.isPublic ?? false, 'listing');
  } else if (isAbsent(value, 'isPublic')) {
    // "Not listed" is the empty starting point, not sample data: set it without a label.
    value.isPublic = false;
    defaulted = true;
  }

  // What the reader saved this session wins: the write went to an address that stores nothing yet.
  const saved = writtenAt(`/organization/${orgId}/profile`);
  if (saved) Object.assign(value, saved);
  if (!used.size && !saved && !defaulted) return null;
  return { body: { ...body, value: JSON.stringify(value) }, used: [...used] };
}

// ─── NEW: GET /organization/{id}/capabilities ────────────────────────────────────────────────────

const FIELDS = ['Faith & ministry', 'Documentary & factual', 'Film & TV drama', 'Children & family', 'Education & e-learning', 'Marketing & advertising', 'Telecom & IVR', 'Animation'];
const TOOLS = ['memoQ', 'Phrase', 'Subtitle Edit', 'Ooona', 'Pro Tools', 'Source-Connect'];
const BANDS = ['7+ yrs', '3–7 yrs', '1–3 yrs', '<1 yr'];

/** A small stable number per id, so the same pair always gets the same sample. */
const pick = (id: number, n: number, salt = 0) => Math.abs(((id + 7) * 2654435761 + salt * 97) | 0) % n;

/**
 * Evidence for the organization's REAL service pairs (so the rows are its own work), plus two
 * self-declared skills. About half the pairs have work done here, one is checked, the rest declared.
 */
export function seedCapabilities(now: Date = new Date()): any {
  const rates = seenServices.flatMap(s => (Array.isArray(s?.rates) ? s.rates : []));
  const evidence = rates.map((r: any, i: number) => {
    const id = Number(r.id) || i + 1;
    const done = pick(id, 2) === 0 ? 0 : 5 + pick(id, 120, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (2 + pick(id, 28, 2)));
    return {
      rateId: id,
      fields: [FIELDS[pick(id, FIELDS.length, 3)]],
      tools: pick(id, 3, 4) === 0 ? [] : [TOOLS[pick(id, TOOLS.length, 5)]],
      quals: [],
      band: BANDS[pick(id, BANDS.length, 6)],
      done,
      lastDone: done ? last.toISOString().slice(0, 10) : null,
      verified: !done && i === rates.length - 1 ? 'Work sample reviewed by WovenWord' : null,
    };
  });
  const skills = [
    { kind: 'sdh_captions', source: 'engenenus', target: null, fields: [], tools: [], quals: ['12 cps or slower (accessibility first)'], band: '1–3 yrs', verified: 'Work sample reviewed by WovenWord' },
    { kind: 'audio_engineering', source: null, target: null, fields: [], tools: ['Pro Tools', 'iZotope RX'], quals: [], band: '3–7 yrs', verified: null },
  ];
  return { evidence, skills };
}

// ─── NEW: GET /ratings/organizations/{id}/summary ────────────────────────────────────────────────

/** The mockup's scores: 38 ratings as a provider, 3 as a client; averages only, never comments. */
export function seedRatings(): any {
  return {
    count: 41,
    average: 4.7,
    asProvider: { count: 38, quality: 4.8, onTime: 4.5, personability: 4.8 },
    asClient: { count: 3, deadlines: 4.5, cooperation: 4.7, personability: 4.7 },
  };
}

// ─── NEW: GET /organization/{id}/portfolio ───────────────────────────────────────────────────────

/** The portfolio page's own sample pieces, so the Overview and the Portfolio page agree. */
export function seedPortfolio(): any[] {
  return PORTFOLIO_DUMMY.map(p => ({
    id: p.id,
    title: p.title,
    category: p.category,
    files: p.files?.length ?? 0,
    views: p.viewCount ?? 0,
    isPublic: p.isPublic,
    color: p.color,
    icon: PORTFOLIO_PIECE_ICON[p.category] ?? 'mat:folder',
  }));
}

// ─── NEW: GET /organization/{id}/teamByFunction ──────────────────────────────────────────────────

/** The mockup's split (21 · 9 · 6 · 4 of 40), scaled to the real team size. */
export function seedTeamFunctions(orgId: number): any[] {
  const total = seenTeam.get(orgId) ?? 40;
  const shares: [string, number][] = [['Linguists and reviewers', 21], ['Voice and audio', 9], ['Project management', 6], ['Administration', 4]];
  let left = total;
  return shares.map(([label, share], i) => {
    const count = i === shares.length - 1 ? Math.max(0, left) : Math.round((share / 40) * total);
    left -= count;
    return { label, count };
  });
}
